import React from 'react';
import { Button, Breadcrumbs, BreadcrumbItem, cn } from '@heroui/react';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { useNavigate } from 'react-router';
import { useTranslations } from '../contexts/translation.context';
import IcTg from '../client/ic-telegram.svg';
import { BurgerIcon, LogoCreatorAuthIc, LogoPartnerIc } from './icons';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useIsMobile } from '../hooks/useMobile';

type HeaderProps = {
  onBurgerClick?: () => void;
};

const Header: React.FC<HeaderProps> = ({ onBurgerClick }) => {
  const isMobile = useIsMobile();
  const { breadcrumbs } = useBreadcrumbs();
  const navigate = useNavigate();
  const { t } = useTranslations();

  const handleBreadcrumbClick = (href: string) => {
    if (href) {
      navigate(href);
    }
  };
  const isPartner = window.location.pathname.startsWith('/partner');
  return (
    <>
      <nav
        className={cn(
          'flex items-end justify-between h-[5.25rem] md:h-16 p-4 py-[1.375rem] md:px-6 md:py-0 md:border-0 border-b border-solid border-neutral-12 md:bg-transparent bg-white',
          isMobile && 'fixed top-0 left-0 z-50 w-full'
        )}
      >
        {/* Breadcrumbs */}
        <div className="flex items-center justify-between w-full md:gap-0 gap-6">
          <div className="md:hidden flex items-center justify-center w-[4.75rem] h-[1.375rem] shrink-0 mr-auto">
            {isPartner ? (
              <LogoPartnerIc className="size-full" />
            ) : (
              <LogoCreatorAuthIc className="size-full" />
            )}
          </div>
          {!isMobile && (
            <div className="flex items-center gap-3">
              {breadcrumbs.length > 0 && (
                <Breadcrumbs separator="/">
                  {breadcrumbs.map((item, index) => (
                    <BreadcrumbItem
                      key={item.href || index}
                      classNames={{
                        separator: 'text-neutral-600 px-4',
                        item: item.isActive
                          ? 'text-neutral-600 cursor-default'
                          : 'text-black cursor-pointer hover:text-partner',
                      }}
                      isLast={item.isActive}
                      isCurrent={item.isActive}
                      onClick={() =>
                        !item.isActive && item.href && handleBreadcrumbClick(item.href)
                      }
                    >
                      {item.label}
                    </BreadcrumbItem>
                  ))}
                </Breadcrumbs>
              )}
            </div>
          )}

          {/* Right side buttons */}
          <div className="flex items-center gap-3">
            <a
              href={isPartner ? 'https://t.me/onlyu_partners' : 'https://t.me/onlyu_support'}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:block"
            >
              <Button
                startContent={<img src={IcTg} alt={t('alt.telegram')} />}
                className="h-10 rounded-3 border-[0.125rem] border-auth-strength-none text-auth-ink text-sm font-semibold leading-5 flex items-center justify-center text-center gap-1 bg-white"
                style={{ boxShadow: 'none' }}
              >
                {t('buttons.support')}
              </Button>
            </a>
            <LanguageSwitcher />
          </div>

          <div className="md:hidden flex items-center shrink-0">
            <button
              type="button"
              aria-label="Burger Menu"
              className="size-6 icon text-2xl"
              onClick={onBurgerClick}
            >
              <BurgerIcon />
            </button>
          </div>
        </div>
      </nav>

      {isMobile && breadcrumbs.length > 0 && (
        <div className="flex items-center gap-3 px-4 pt-6">
          <Breadcrumbs separator="/" classNames={{ base: 'flex-wrap' }}>
            {breadcrumbs.map((item, index) => (
              <BreadcrumbItem
                key={item.href || index}
                classNames={{
                  separator: 'text-neutral-600 px-2 md:px-4',
                  item: item.isActive
                    ? 'text-neutral-600 cursor-default'
                    : 'text-black cursor-pointer hover:text-partner',
                }}
                isLast={item.isActive}
                isCurrent={item.isActive}
                onClick={() => !item.isActive && item.href && handleBreadcrumbClick(item.href)}
              >
                {item.label}
              </BreadcrumbItem>
            ))}
          </Breadcrumbs>
        </div>
      )}
    </>
  );
};

export default Header;
